/**
 * Feature 021: Client-Agnostic Matchmaking Constants
 *
 * Special constant to represent "Generic Search" mode where no specific client is selected.
 * This allows advisors to search for providers without tying the search to a specific client.
 */

/**
 * Special constant ID for generic search mode
 * This is used internally in the frontend and converted to null when calling backend API
 */
export const GENERIC_SEARCH_ID = 'GENERIC_SEARCH';

/**
 * Type for selected client ID
 * - string (UUID): actual client user ID
 * - GENERIC_SEARCH_ID: generic search mode (no specific client)
 */
export type SelectedClientId = string | typeof GENERIC_SEARCH_ID;

/**
 * UUID v4 regex pattern for validation
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Checks if a string is a valid UUID
 */
export function isValidUuid(value: string | null | undefined): boolean {
  if (!value) return false;
  return UUID_REGEX.test(value);
}

/**
 * Type guard to check if a client ID should be treated as a generic search
 * 
 * Returns true if:
 * - clientId is the GENERIC_SEARCH_ID constant
 * - clientId is null/undefined
 * - clientId is not a valid UUID (e.g., temporary fallback IDs)
 * 
 * This ensures that only valid UUIDs are sent to the backend as client IDs,
 * preventing validation errors for temporary or invalid IDs.
 */
export function isGenericSearch(clientId: SelectedClientId | null | undefined): boolean {
  // Explicit generic search constant
  if (clientId === GENERIC_SEARCH_ID) return true;
  
  // Null/undefined should be treated as generic
  if (!clientId) return true;
  
  // Non-UUID values (like temporary fallback IDs) should be treated as generic
  // to prevent backend validation errors
  if (!isValidUuid(clientId)) return true;
  
  return false;
}
